import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Check } from 'lucide-react';
import { CHAT_MODELS, IMAGE_MODELS, PROVIDER_COLORS } from '../models';
import { API } from '../App';

export default function SettingsModal({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings);
  }, []);

  async function save() {
    setSaving(true);
    await fetch(`${API}/api/settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 800);
  }

  if (!settings) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.93, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93 }}
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto',
          background: 'var(--bg-3)', border: '1px solid var(--border)',
          borderRadius: 20, boxShadow: 'var(--card-shadow-hover)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0,
          background: 'var(--bg-3)', zIndex: 1,
        }}>
          <Settings size={16} color="#6366f1" />
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Settings</h2>
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={onClose}
            style={{ background: 'var(--bg-4)', border: '1px solid var(--border)', borderRadius: 8, padding: 6, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
            <X size={14} />
          </motion.button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Chat Model */}
          <Section title="Chat Model" subtitle="Used for the AI research sidebar">
            <ModelPicker
              models={CHAT_MODELS}
              value={settings.chatModel}
              onChange={v => setSettings(s => ({ ...s, chatModel: v }))}
            />
          </Section>

          {/* Image Model */}
          <Section title="Image Generation Model" subtitle="Claude can't generate images — this model handles image requests in the sidebar">
            <ModelPicker
              models={IMAGE_MODELS}
              value={settings.imageModel}
              onChange={v => setSettings(s => ({ ...s, imageModel: v }))}
            />
          </Section>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: 'var(--bg-3)' }}>
          <button onClick={onClose} style={cancelBtn}>Cancel</button>
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={save}
            style={{
              padding: '9px 20px', borderRadius: 9, border: 'none',
              background: saved ? '#10b981' : '#6366f1',
              color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7,
              transition: 'background 0.2s',
            }}
          >
            {saved ? <><Check size={14} /> Saved</> : saving ? 'Saving…' : 'Save Settings'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{subtitle}</div>
      </div>
      {children}
    </div>
  );
}

function ModelPicker({ models, value, onChange }) {
  // Group by provider
  const groups = {};
  models.forEach(m => {
    if (!groups[m.provider]) groups[m.provider] = [];
    groups[m.provider].push(m);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value && !models.some(model => model.id === value) && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>
          Current selection: {value} (legacy). Choose a model below to update it.
        </div>
      )}
      {Object.entries(groups).map(([provider, provModels]) => (
        <div key={provider}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
            color: PROVIDER_COLORS[provider] || '#6366f1',
            textTransform: 'uppercase', marginBottom: 5, marginTop: 4,
          }}>
            {provider}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {provModels.map(m => {
              const selected = value === m.id;
              return (
                <motion.div
                  key={m.id}
                  whileHover={{ x: 2 }}
                  onClick={() => onChange(m.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 10, cursor: 'pointer',
                    background: selected ? `${PROVIDER_COLORS[provider] || '#6366f1'}15` : 'var(--bg-4)',
                    border: `1px solid ${selected ? (PROVIDER_COLORS[provider] || '#6366f1') + '45' : 'var(--border)'}`,
                    transition: 'all 0.12s',
                  }}
                >
                  {/* Radio dot */}
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${selected ? (PROVIDER_COLORS[provider] || '#6366f1') : 'var(--border)'}`,
                    background: selected ? (PROVIDER_COLORS[provider] || '#6366f1') : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: selected ? 600 : 400, color: 'var(--text-primary)' }}>
                      {m.name}
                      {m.default && <span style={{ fontSize: 10, color: 'var(--text-faint)', fontWeight: 400, marginLeft: 6 }}>default</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.id}</div>
                  </div>
                  {m.vision && (
                    <span style={{ fontSize: 10, color: '#10b981', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                      vision
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const cancelBtn = {
  padding: '9px 16px', borderRadius: 9, background: 'var(--bg-4)',
  border: '1px solid var(--border)', color: 'var(--text-muted)',
  cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
};
